import { useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AlertTriangle, CalendarClock, CheckCircle2, Download, FileText, Layers3, Receipt, Repeat2, ShoppingCart, Sparkles, Timer, Users, TrendingUp } from 'lucide-react'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { getErrorMessage } from '@/lib/api'
import {
  buildQuotationPresetCounts,
  buildCommerceQuotationPresetPath,
  buildSalesDashboardPresetPath,
  buildOptimisticQuotationPatch,
  filterQuotationsByPreset,
  formatQuotationMoney,
  getQuotationBookingMetaClass,
  getQuotationActionButtonClass,
  getQuotationPresetChipClass,
  getQuotationPresetCountPillClass,
  getQuotationPresetFilterPanelClass,
  getQuotationPresetKpiAccentClass,
  getQuotationPresetKpiCardClass,
  getQuotationQuickActionClass,
  getQuotationSignalBadgeClass,
  getQuotationPresetSurfaceClass,
  getQuotationContractMetaClass,
  hasRealBookingReference,
  normalizeQuotationPreset,
  OPTIMISTIC_BOOKING_REFERENCE,
  QUOTATION_CARD_CLASS,
  QUOTATION_STATUS_BADGE_CLASS,
  restoreQuotationStatusCache,
  SALES_DASHBOARD_QUOTATION_PRESET_QUERY_PARAM,
  type QuotationActionKind,
  type QuotationListPreset,
  updateQuotationStatusCache,
} from '@/lib/quotation-sales'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { ordersApi } from '@/services/orders'
import { quotationsApi } from '@/services/quotations'
import { salesApi } from '@/services/sales'

const statValue = (value: unknown) => {
  const numeric = Number(value ?? 0)
  return Number.isFinite(numeric) ? numeric : 0
}

const parseDate = (value: unknown) => {
  if (!value) return null
  const parsed = new Date(String(value))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const monthKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}`

type TrendRange = '6m' | '12m' | 'ytd'

const buildMonthFrames = (range: TrendRange) => {
  const frames: Array<{ key: string; label: string }> = []
  const now = new Date()

  if (range === 'ytd') {
    for (let month = 0; month <= now.getMonth(); month += 1) {
      const date = new Date(now.getFullYear(), month, 1)
      frames.push({ key: monthKey(date), label: date.toLocaleDateString(undefined, { month: 'short' }) })
    }
    return frames
  }

  const count = range === '12m' ? 12 : 6
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1)
    frames.push({ key: monthKey(date), label: date.toLocaleDateString(undefined, { month: 'short' }) })
  }
  return frames
}

const contactKey = (enquiry: { email?: string | null; phone?: string | null; name?: string | null }) => {
  const email = String(enquiry.email ?? '').trim().toLowerCase()
  const phone = String(enquiry.phone ?? '').replace(/\D/g, '')
  const name = String(enquiry.name ?? '').trim().toLowerCase()
  return email || phone || name || ''
}

export default function SalesDashboardPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const queryClient = useQueryClient()
  const [activeQuotationAction, setActiveQuotationAction] = useState<{ id: string | number; action: QuotationActionKind } | null>(null)
  const [trendRange, setTrendRange] = useState<TrendRange>('6m')
  const quotationPreset = normalizeQuotationPreset(searchParams.get(SALES_DASHBOARD_QUOTATION_PRESET_QUERY_PARAM))

  const quotationsQuery = useQuery({
    queryKey: ['sales-dashboard', 'quotations', serviceInstanceId],
    queryFn: () => quotationsApi.list({ service_instance_id: serviceInstanceId, page_size: 200 }),
  })

  const enquiriesQuery = useQuery({
    queryKey: ['sales-dashboard', 'enquiries', serviceInstanceId],
    queryFn: () => salesApi.listEnquiries({ service_instance_id: serviceInstanceId }),
  })

  const followupsQuery = useQuery({
    queryKey: ['sales-dashboard', 'followups', serviceInstanceId],
    queryFn: () => salesApi.listFollowups({ service_instance_id: serviceInstanceId }),
  })

  const bookingsQuery = useQuery({
    queryKey: ['sales-dashboard', 'bookings', serviceInstanceId],
    queryFn: () => ordersApi.listOrders({ service_instance_id: serviceInstanceId, page_size: 1 }),
  })

  const unpaidBookingsQuery = useQuery({
    queryKey: ['sales-dashboard', 'unpaid-bookings', serviceInstanceId],
    queryFn: () => ordersApi.listOrders({ service_instance_id: serviceInstanceId, page_size: 1, is_paid: false }),
  })

  const orderStatsQuery = useQuery({
    queryKey: ['sales-dashboard', 'order-stats', serviceInstanceId],
    queryFn: () => ordersApi.getOrderStats({ service_instance_id: serviceInstanceId }),
  })

  const bookingsTrendQuery = useQuery({
    queryKey: ['sales-dashboard', 'bookings-trend', serviceInstanceId],
    queryFn: () => ordersApi.listOrders({ service_instance_id: serviceInstanceId, page_size: 500 }),
  })

  const statusBreakdown = useMemo(() => {
    const rows = orderStatsQuery.data?.order_count_stat ?? []
    return rows
      .map((row) => ({
        label: String(row.label || row.status || 'Unknown'),
        count: statValue(row.count),
      }))
      .filter((row) => row.count > 0)
      .sort((left, right) => right.count - left.count)
      .slice(0, 6)
  }, [orderStatsQuery.data?.order_count_stat])

  const quotationRows = quotationsQuery.data?.results ?? []
  const enquiries = enquiriesQuery.data?.results ?? []
  const followups = followupsQuery.data?.results ?? []
  const filteredQuotations = useMemo(() => filterQuotationsByPreset(quotationRows, quotationPreset), [quotationPreset, quotationRows])
  const quotationPresetCounts = useMemo(() => buildQuotationPresetCounts(quotationRows), [quotationRows])
  const recentQuotations = useMemo(() => [...filteredQuotations]
    .sort((left, right) => new Date(right.updated_on || right.created_on || 0).getTime() - new Date(left.updated_on || left.created_on || 0).getTime())
    .slice(0, 6), [filteredQuotations])

  const quotationStats = useMemo(() => {
    const accepted = quotationRows.filter((row) => row.status === 'accepted').length
    const converted = quotationRows.filter((row) => row.status === 'converted').length
    const withContract = quotationRows.filter((row) => Boolean(row.contract_document_file_url || row.contract_document_url)).length
    const linkedToEnquiry = quotationRows.filter((row) => Boolean(row.enquiry)).length
    const linkedToFollowup = quotationRows.filter((row) => Boolean(row.source_followup)).length
    return { accepted, converted, withContract, linkedToEnquiry, linkedToFollowup }
  }, [quotationRows])

  const enquiryToQuoteRate = enquiries.length > 0 ? Math.round((quotationStats.linkedToEnquiry / enquiries.length) * 100) : 0
  const acceptedToBookingRate = quotationStats.accepted > 0 || quotationStats.converted > 0
    ? Math.round((quotationStats.converted / Math.max(quotationStats.accepted + quotationStats.converted, 1)) * 100)
    : 0

  const openFollowups = followups.filter((row) => row.status === 'open').length
  const isLoading = quotationsQuery.isLoading || bookingsQuery.isLoading || unpaidBookingsQuery.isLoading || enquiriesQuery.isLoading || followupsQuery.isLoading
  const formatPresetCount = (preset: QuotationListPreset) => (quotationsQuery.isLoading ? '...' : quotationPresetCounts[preset])
  const trendRangeLabel = trendRange === 'ytd' ? 'YTD' : trendRange.toUpperCase()

  const monthFrames = useMemo(() => {
    return buildMonthFrames(trendRange)
  }, [trendRange])

  const trendRows = useMemo(() => {
    const monthMap: Record<string, { month: string; enquiries: number; siteVisits: number; quotations: number; contracts: number; bookings: number }> = {}

    for (const month of monthFrames) {
      monthMap[month.key] = { month: month.label, enquiries: 0, siteVisits: 0, quotations: 0, contracts: 0, bookings: 0 }
    }

    for (const enquiry of enquiries) {
      const createdAt = parseDate((enquiry as { created_at?: string | null }).created_at)
      if (!createdAt) continue
      const key = monthKey(createdAt)
      if (monthMap[key]) monthMap[key].enquiries += 1
    }

    for (const followup of followups) {
      if ((followup as { activity_type?: string | null }).activity_type !== 'site_visit') continue
      const createdAt = parseDate((followup as { created_at?: string | null }).created_at)
      if (!createdAt) continue
      const key = monthKey(createdAt)
      if (monthMap[key]) monthMap[key].siteVisits += 1
    }

    for (const quotation of quotationRows) {
      const createdAt = parseDate(quotation.created_on)
      if (!createdAt) continue
      const createdKey = monthKey(createdAt)
      if (monthMap[createdKey]) monthMap[createdKey].quotations += 1

      if (quotation.effective_contract_document_url || quotation.contract_document_url || quotation.contract_document_file_url) {
        const contractAt = parseDate(quotation.updated_on) ?? createdAt
        const contractKey = monthKey(contractAt)
        if (monthMap[contractKey]) monthMap[contractKey].contracts += 1
      }
    }

    for (const booking of bookingsTrendQuery.data?.results ?? []) {
      const createdAt = parseDate((booking as { created_on?: string | null }).created_on)
      if (!createdAt) continue
      const key = monthKey(createdAt)
      if (monthMap[key]) monthMap[key].bookings += 1
    }

    return monthFrames.map((month) => monthMap[month.key])
  }, [bookingsTrendQuery.data?.results, enquiries, followups, monthFrames, quotationRows])

  const operationsRows = useMemo(() => {
    const monthMap: Record<string, { month: string; recurring: number; singleTouch: number; avgHours: number; totalHours: number; completedCount: number }> = {}
    for (const month of monthFrames) {
      monthMap[month.key] = { month: month.label, recurring: 0, singleTouch: 0, avgHours: 0, totalHours: 0, completedCount: 0 }
    }

    const enquiryContactCounts: Record<string, number> = {}
    for (const enquiry of enquiries) {
      const key = contactKey(enquiry)
      if (!key) continue
      enquiryContactCounts[key] = (enquiryContactCounts[key] ?? 0) + 1
    }

    const followupCountByEnquiry: Record<number, number> = {}
    for (const followup of followups) {
      if (!followup.enquiry_id) continue
      followupCountByEnquiry[followup.enquiry_id] = (followupCountByEnquiry[followup.enquiry_id] ?? 0) + 1
    }

    for (const enquiry of enquiries) {
      const createdAt = parseDate(enquiry.created_at)
      if (!createdAt) continue
      const key = monthKey(createdAt)
      if (!monthMap[key]) continue

      const enquiryKey = contactKey(enquiry)
      if (enquiryKey && (enquiryContactCounts[enquiryKey] ?? 0) > 1) {
        monthMap[key].recurring += 1
      }

      const followupCount = followupCountByEnquiry[enquiry.id] ?? 0
      if (followupCount <= 1) {
        monthMap[key].singleTouch += 1
      }
    }

    for (const followup of followups) {
      if (followup.status !== 'completed' || !followup.completed_at) continue
      const completedAt = parseDate(followup.completed_at)
      const createdAt = parseDate(followup.created_at)
      if (!completedAt || !createdAt) continue
      const key = monthKey(completedAt)
      if (!monthMap[key]) continue
      const durationHours = Math.max(0, (completedAt.getTime() - createdAt.getTime()) / 3600000)
      monthMap[key].totalHours += durationHours
      monthMap[key].completedCount += 1
    }

    return monthFrames.map((month) => {
      const row = monthMap[month.key]
      row.avgHours = row.completedCount > 0 ? Number((row.totalHours / row.completedCount).toFixed(1)) : 0
      return {
        month: row.month,
        recurring: row.recurring,
        singleTouch: row.singleTouch,
        avgHours: row.avgHours,
      }
    })
  }, [enquiries, followups, monthFrames])

  const downloadCsv = (fileName: string, rows: Array<Record<string, string | number>>) => {
    if (rows.length === 0) return
    const generatedAt = new Date().toISOString()
    const serviceScope = serviceInstanceId ?? 'all'
    const headers = Object.keys(rows[0])
    const lines = [
      '"Sales Export"',
      `"Range","${trendRangeLabel}"`,
      `"Generated At","${generatedAt}"`,
      `"Service Instance","${serviceScope}"`,
      '',
      headers.join(','),
    ]
    for (const row of rows) {
      const values = headers.map((header) => {
        const value = row[header] ?? ''
        const text = String(value).replace(/"/g, '""')
        return `"${text}"`
      })
      lines.push(values.join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()
    URL.revokeObjectURL(url)
  }

  const downloadCombinedCsv = () => {
    const lines: string[] = []
    const generatedAt = new Date().toISOString()
    const serviceScope = serviceInstanceId ?? 'all'

    lines.push(`"Sales Combined Report (${trendRangeLabel})"`)
    lines.push(`"Range","${trendRangeLabel}"`)
    lines.push(`"Generated At","${generatedAt}"`)
    lines.push(`"Service Instance","${serviceScope}"`)
    lines.push('')

    lines.push('"Sales Momentum"')
    lines.push('"month","enquiries","siteVisits","quotations","contracts","bookings"')
    for (const row of trendRows) {
      lines.push(`"${row.month}","${row.enquiries}","${row.siteVisits}","${row.quotations}","${row.contracts}","${row.bookings}"`)
    }

    lines.push('')
    lines.push('"Sales Operations Signals"')
    lines.push('"month","recurring","singleTouch","avgHours"')
    for (const row of operationsRows) {
      lines.push(`"${row.month}","${row.recurring}","${row.singleTouch}","${row.avgHours}"`)
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `sales-dashboard-combined-${trendRange}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const quotationQueryKeys = [
    ['sales-dashboard', 'quotations', serviceInstanceId],
    ['commerce', 'quotations', serviceInstanceId],
    ['sales', 'quotations-by-enquiry', serviceInstanceId],
    ['sales', 'followup-quotations', serviceInstanceId],
  ] as const

  const invalidateQuotationViews = () => {
    queryClient.invalidateQueries({ queryKey: ['sales-dashboard', 'quotations', serviceInstanceId] })
    queryClient.invalidateQueries({ queryKey: ['commerce', 'quotations', serviceInstanceId] })
    queryClient.invalidateQueries({ queryKey: ['sales', 'quotations-by-enquiry', serviceInstanceId] })
    queryClient.invalidateQueries({ queryKey: ['sales', 'followup-quotations', serviceInstanceId] })
  }

  const acceptQuotationMutation = useMutation({
    mutationFn: (quotationId: string | number) => quotationsApi.accept(quotationId, serviceInstanceId),
    onMutate: (quotationId) => {
      setActiveQuotationAction({ id: quotationId, action: 'accept' })
      return {
        snapshots: updateQuotationStatusCache(queryClient, quotationId, buildOptimisticQuotationPatch('accept'), quotationQueryKeys),
      }
    },
    onSuccess: () => {
      toast.success('Quotation accepted')
      invalidateQuotationViews()
    },
    onError: (error, _quotationId, context) => {
      restoreQuotationStatusCache(queryClient, context?.snapshots)
      toast.error(getErrorMessage(error))
    },
    onSettled: () => setActiveQuotationAction(null),
  })

  const convertQuotationMutation = useMutation({
    mutationFn: (quotationId: string | number) => quotationsApi.convert(quotationId, serviceInstanceId),
    onMutate: (quotationId) => {
      setActiveQuotationAction({ id: quotationId, action: 'convert' })
      return {
        snapshots: updateQuotationStatusCache(queryClient, quotationId, buildOptimisticQuotationPatch('convert'), quotationQueryKeys),
      }
    },
    onSuccess: (result, quotationId) => {
      updateQuotationStatusCache(queryClient, quotationId, {
        status: 'converted',
        converted_at: new Date().toISOString(),
        order_reference: result.order_reference ?? undefined,
        order_id: result.order_id ?? undefined,
        order: result.order_id ?? undefined,
      }, quotationQueryKeys)
      toast.success('Quotation converted to booking')
      invalidateQuotationViews()
      if (result.order_reference) {
        window.open(withServiceInstanceId(`/services/orders/${result.order_reference}`, serviceInstanceId), '_blank', 'noopener,noreferrer')
      }
    },
    onError: (error, _quotationId, context) => {
      restoreQuotationStatusCache(queryClient, context?.snapshots)
      toast.error(getErrorMessage(error))
    },
    onSettled: () => setActiveQuotationAction(null),
  })

  const applyPreset = (preset: QuotationListPreset) => {
    const nextParams = new URLSearchParams(searchParams)
    if (preset === 'all') {
      nextParams.delete(SALES_DASHBOARD_QUOTATION_PRESET_QUERY_PARAM)
    } else {
      nextParams.set(SALES_DASHBOARD_QUOTATION_PRESET_QUERY_PARAM, preset)
    }
    setSearchParams(nextParams, { replace: true })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sales</h1>
          <p className="text-sm text-muted-foreground">
            Monitor enquiry conversion, quotations, bookings, and payment follow-through in one place.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge variant="outline" className={getQuotationSignalBadgeClass('open')}>
              <Sparkles className="h-3.5 w-3.5" />
              Sky marks open quotation flow
            </Badge>
            <Badge variant="outline" className={getQuotationSignalBadgeClass('contract')}>
              <AlertTriangle className="h-3.5 w-3.5" />
              Amber marks contract follow-up
            </Badge>
            <Badge variant="outline" className={getQuotationSignalBadgeClass('ready')}>
              <CheckCircle2 className="h-3.5 w-3.5" />
              Emerald marks booking-ready work
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" className={getQuotationQuickActionClass('open')}>
            <Link to={withServiceInstanceId('/services/quotations/new', serviceInstanceId)}>
              New Quotation
            </Link>
          </Button>
          <Button asChild variant="outline" className={getQuotationQuickActionClass('ready')}>
            <Link to={withServiceInstanceId('/services/orders', serviceInstanceId)}>
              View Bookings
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <Card className="border-primary/25 bg-gradient-to-br from-primary/10 via-background to-background">
          <div className="h-1 w-full rounded-t-xl bg-primary" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.12em]">Enquiries</CardTitle>
            <Users className="h-3.5 w-3.5 text-primary" />
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="text-xl font-semibold text-primary">{isLoading ? '...' : enquiries.length}</div>
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-gradient-to-br from-primary/6 via-background to-background">
          <div className="h-1 w-full rounded-t-xl bg-primary/80" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.12em]">Quotations</CardTitle>
            <FileText className="h-3.5 w-3.5 text-primary" />
          </CardHeader>
          <CardContent className="space-y-2 pt-0 pb-3">
            <div className="text-xl font-semibold text-primary">{isLoading ? '...' : statValue(quotationsQuery.data?.count)}</div>
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <Link to={withServiceInstanceId(buildCommerceQuotationPresetPath('all'), serviceInstanceId)} className="font-semibold text-slate-700 hover:text-slate-900 hover:underline">All quotes</Link>
              <Link to={withServiceInstanceId(buildCommerceQuotationPresetPath('open-quotes'), serviceInstanceId)} className="font-semibold text-primary hover:opacity-90 hover:underline">Open quotes</Link>
              <Link to={withServiceInstanceId(buildCommerceQuotationPresetPath('needs-contract'), serviceInstanceId)} className="font-semibold text-warning hover:opacity-90 hover:underline">Needs contract</Link>
            </div>
          </CardContent>
        </Card>

        <Card className="border-success/30 bg-gradient-to-br from-success/12 via-background to-background">
          <div className="h-1 w-full rounded-t-xl bg-success" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.12em]">Bookings</CardTitle>
            <ShoppingCart className="h-3.5 w-3.5 text-success" />
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="text-xl font-semibold text-success">{isLoading ? '...' : statValue(bookingsQuery.data?.count)}</div>
          </CardContent>
        </Card>

        <Card className="border-warning/35 bg-gradient-to-br from-warning/14 via-background to-background">
          <div className="h-1 w-full rounded-t-xl bg-warning" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.12em]">Unpaid Bookings</CardTitle>
            <Receipt className="h-3.5 w-3.5 text-warning" />
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="text-xl font-semibold text-warning">{isLoading ? '...' : statValue(unpaidBookingsQuery.data?.count)}</div>
          </CardContent>
        </Card>

        <Card className="border-secondary bg-gradient-to-br from-secondary/60 via-background to-background">
          <div className="h-1 w-full rounded-t-xl bg-secondary-foreground/50" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.12em]">Follow Ups</CardTitle>
            <CalendarClock className="h-3.5 w-3.5 text-secondary-foreground" />
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            <div className="text-xl font-semibold text-secondary-foreground">{isLoading ? '...' : openFollowups}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className={getQuotationPresetKpiCardClass(quotationPreset)}>
          <div className={`h-1 w-full rounded-t-xl ${getQuotationPresetKpiAccentClass(quotationPreset)}`} />
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Conversion Snapshot
            </CardTitle>
            <CardDescription>How enquiries are progressing into quotations and bookings.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 p-4">
              <div className="text-xs uppercase text-muted-foreground">Enquiry to quotation</div>
              <div className="mt-1 text-2xl font-semibold">{enquiryToQuoteRate}%</div>
              <div className="text-xs text-muted-foreground">{quotationStats.linkedToEnquiry} of {enquiries.length} enquiries produced quotations</div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
              <div className="text-xs uppercase text-muted-foreground">Accepted to booking</div>
              <div className="mt-1 text-2xl font-semibold">{acceptedToBookingRate}%</div>
              <div className="text-xs text-muted-foreground">{quotationStats.converted} converted from accepted quotations</div>
            </div>
          </CardContent>
        </Card>

        <Card className={getQuotationPresetKpiCardClass(quotationPreset)}>
          <div className={`h-1 w-full rounded-t-xl ${getQuotationPresetKpiAccentClass(quotationPreset)}`} />
          <CardHeader>
            <CardTitle>Quotation Coverage</CardTitle>
            <CardDescription>Sales linkage and contract readiness for quotations.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">Accepted: {quotationStats.accepted}</Badge>
            <Badge variant="outline" className="border-teal-200 bg-teal-50 text-teal-800">Converted: {quotationStats.converted}</Badge>
            <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-800">With contract: {quotationStats.withContract}</Badge>
            <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-800">Linked to enquiry: {quotationStats.linkedToEnquiry}</Badge>
            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-900">Linked to follow-up: {quotationStats.linkedToFollowup}</Badge>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <Link to={withServiceInstanceId(buildCommerceQuotationPresetPath('open-quotes'), serviceInstanceId)} className="font-semibold text-primary hover:opacity-90 hover:underline">Review open quotes</Link>
              <Link to={withServiceInstanceId(buildCommerceQuotationPresetPath('needs-contract'), serviceInstanceId)} className="font-semibold text-warning hover:opacity-90 hover:underline">Fill missing contracts</Link>
              <Link to={withServiceInstanceId(buildSalesDashboardPresetPath('converted'), serviceInstanceId)} className="font-semibold text-success hover:opacity-90 hover:underline">Open converted view</Link>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-primary/25 bg-gradient-to-br from-primary/8 via-background to-background">
        <CardHeader>
          <CardTitle>Sales Momentum ({trendRangeLabel})</CardTitle>
          <CardDescription>Monthly trend for enquiries, site visits, quotations, contracts, and bookings.</CardDescription>
          <div className="flex flex-wrap items-center gap-2">
            {(['6m', '12m', 'ytd'] as const).map((range) => (
              <Button
                key={range}
                size="sm"
                variant="outline"
                className={trendRange === range ? getQuotationQuickActionClass('open') : getQuotationQuickActionClass('neutral')}
                onClick={() => setTrendRange(range)}
              >
                {range === 'ytd' ? 'YTD' : range.toUpperCase()}
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              className={getQuotationQuickActionClass('ready')}
              onClick={downloadCombinedCsv}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              Export Combined
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={getQuotationQuickActionClass('contract')}
              onClick={() => downloadCsv(`sales-momentum-${trendRange}.csv`, trendRows)}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendRows} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fill: 'var(--foreground)', fontSize: 12 }} axisLine={{ stroke: 'var(--border)' }} tickLine={{ stroke: 'var(--border)' }} />
                <YAxis allowDecimals={false} tick={{ fill: 'var(--foreground)', fontSize: 12 }} axisLine={{ stroke: 'var(--border)' }} tickLine={{ stroke: 'var(--border)' }} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}
                  labelStyle={{ color: 'var(--foreground)', fontWeight: 600 }}
                />
                <Legend />
                <Line type="monotone" dataKey="enquiries" name="Enquiries" stroke="var(--chart-1)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="siteVisits" name="Site Visits" stroke="var(--chart-3)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="quotations" name="Quotations" stroke="var(--chart-5)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="contracts" name="Contracts" stroke="var(--warning)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="bookings" name="Bookings" stroke="var(--success)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid gap-2 rounded-lg border border-indigo-200 bg-white/80 p-3 text-xs text-slate-700 sm:grid-cols-2 lg:grid-cols-3">
            <div><span className="font-semibold text-sky-700">Enquiries:</span> New customer leads created in the month.</div>
            <div><span className="font-semibold text-orange-700">Site Visits:</span> Follow-ups tagged as site visit activity.</div>
            <div><span className="font-semibold text-violet-700">Quotations:</span> New quotations created in that month.</div>
            <div><span className="font-semibold text-amber-700">Contracts:</span> Quotations with contract attachment recorded in that period.</div>
            <div><span className="font-semibold text-emerald-700">Bookings:</span> Orders created from sales flow.</div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-primary/25 bg-gradient-to-br from-primary/8 via-background to-background">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Repeat2 className="h-4 w-4 text-primary" />
            Sales Operations Signals ({trendRangeLabel})
          </CardTitle>
          <CardDescription>Recurring leads, single-touch enquiry counts, and average follow-up working hours.</CardDescription>
          <div>
            <Button
              size="sm"
              variant="outline"
              className={getQuotationQuickActionClass('contract')}
              onClick={() => downloadCsv(`sales-operations-signals-${trendRange}.csv`, operationsRows)}
            >
              <Download className="mr-1 h-3.5 w-3.5" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">Recurring leads</Badge>
            <Badge variant="outline" className="border-secondary bg-secondary/70 text-secondary-foreground">Single-touch leads</Badge>
            <Badge variant="outline" className="border-warning/30 bg-warning/10 text-warning">Avg working hours</Badge>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={operationsRows} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fill: 'var(--foreground)', fontSize: 12 }} axisLine={{ stroke: 'var(--border)' }} tickLine={{ stroke: 'var(--border)' }} />
                <YAxis yAxisId="count" allowDecimals={false} tick={{ fill: 'var(--foreground)', fontSize: 12 }} axisLine={{ stroke: 'var(--border)' }} tickLine={{ stroke: 'var(--border)' }} />
                <YAxis yAxisId="hours" orientation="right" allowDecimals={false} tick={{ fill: 'var(--foreground)', fontSize: 12 }} axisLine={{ stroke: 'var(--border)' }} tickLine={{ stroke: 'var(--border)' }} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}
                  labelStyle={{ color: 'var(--foreground)', fontWeight: 600 }}
                />
                <Legend />
                <Line yAxisId="count" type="monotone" dataKey="recurring" name="Recurring" stroke="var(--chart-5)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line yAxisId="count" type="monotone" dataKey="singleTouch" name="Single Touch" stroke="var(--chart-2)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line yAxisId="hours" type="monotone" dataKey="avgHours" name="Avg Hours" stroke="var(--warning)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2">
              <div className="text-[11px] uppercase tracking-[0.14em] text-primary">Recurring (latest)</div>
              <div className="mt-1 text-lg font-semibold text-primary">{operationsRows.at(-1)?.recurring ?? 0}</div>
            </div>
            <div className="rounded-lg border border-secondary bg-secondary/80 px-3 py-2">
              <div className="text-[11px] uppercase tracking-[0.14em] text-secondary-foreground">Single Touch (latest)</div>
              <div className="mt-1 text-lg font-semibold text-secondary-foreground">{operationsRows.at(-1)?.singleTouch ?? 0}</div>
            </div>
            <div className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2">
              <div className="flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-warning">
                <Timer className="h-3.5 w-3.5" />
                Avg Working Hours (latest)
              </div>
              <div className="mt-1 text-lg font-semibold text-warning">{operationsRows.at(-1)?.avgHours ?? 0}h</div>
            </div>
          </div>
          <div className="grid gap-2 rounded-lg border border-border bg-card/80 p-3 text-xs text-foreground/80 sm:grid-cols-2 lg:grid-cols-3">
            <div><span className="font-semibold text-primary">Recurring:</span> Leads where contact identity appears more than once.</div>
            <div><span className="font-semibold text-secondary-foreground">Single Touch:</span> Leads with zero or one follow-up recorded.</div>
            <div><span className="font-semibold text-warning">Avg Hours:</span> Average elapsed hours from follow-up creation to completion.</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Booking Status Snapshot</CardTitle>
          <CardDescription>Top booking statuses for the selected service instance.</CardDescription>
        </CardHeader>
        <CardContent>
          {orderStatsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading status data...</p>
          ) : statusBreakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">No booking status data available yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {statusBreakdown.map((status) => (
                <Badge key={status.label} variant="secondary" className="px-3 py-1">
                  {status.label}: {status.count}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className={getQuotationPresetSurfaceClass(quotationPreset)}>
        <CardHeader>
          <CardTitle>Quotation Drilldown</CardTitle>
          <CardDescription>Recent quotations with inline actions for sales leads that need attention.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`mb-3 rounded-xl border p-3 shadow-sm ${getQuotationPresetFilterPanelClass(quotationPreset)}`}>
            <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('all', quotationPreset === 'all')} onClick={() => applyPreset('all')}>
              <Layers3 className="h-3.5 w-3.5" />
              <span>All</span>
              <span className={getQuotationPresetCountPillClass('all', quotationPreset === 'all')}>{formatPresetCount('all')}</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('open-quotes', quotationPreset === 'open-quotes')} onClick={() => applyPreset('open-quotes')}>
              <FileText className="h-3.5 w-3.5" />
              <span>Open Quotes</span>
              <span className={getQuotationPresetCountPillClass('open-quotes', quotationPreset === 'open-quotes')}>{formatPresetCount('open-quotes')}</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('needs-contract', quotationPreset === 'needs-contract')} onClick={() => applyPreset('needs-contract')}>
              <Receipt className="h-3.5 w-3.5" />
              <span>Needs Contract</span>
              <span className={getQuotationPresetCountPillClass('needs-contract', quotationPreset === 'needs-contract')}>{formatPresetCount('needs-contract')}</span>
            </Button>
            <Button size="sm" variant="outline" className={getQuotationPresetChipClass('converted', quotationPreset === 'converted')} onClick={() => applyPreset('converted')}>
              <ShoppingCart className="h-3.5 w-3.5" />
              <span>Converted</span>
              <span className={getQuotationPresetCountPillClass('converted', quotationPreset === 'converted')}>{formatPresetCount('converted')}</span>
            </Button>
            </div>
          </div>
          <div className="mb-3 text-sm text-muted-foreground">Showing {recentQuotations.length} of {filteredQuotations.length} matching quotations.</div>
          {quotationsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading quotations...</p>
          ) : recentQuotations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No quotations available yet.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {recentQuotations.map((quotation) => (
                <div key={String(quotation.id)} className={`rounded-xl border bg-background/95 p-4 shadow-sm backdrop-blur-[1px] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${QUOTATION_CARD_CLASS[quotation.status] ?? 'border-slate-200'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Link to={withServiceInstanceId(`/services/quotations/${quotation.id}`, serviceInstanceId)} className="text-sm font-semibold tracking-tight text-slate-900 hover:text-sky-700 hover:underline">
                        {quotation.quotation_number}
                      </Link>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {[quotation.customer_name, quotation.enquiry_name || quotation.source_followup_title].filter(Boolean).join(' · ') || 'Unlinked quotation'}
                      </div>
                    </div>
                    <Badge variant="outline" className={QUOTATION_STATUS_BADGE_CLASS[quotation.status] ?? 'border-slate-200 text-slate-700'}>{quotation.status}</Badge>
                  </div>
                  <div className="mt-4 space-y-2.5 text-sm text-muted-foreground">
                    <div className="rounded-lg border border-white/70 bg-white/85 px-3 py-2.5 shadow-sm">
                      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Total</div>
                      <div className="font-medium text-foreground">{formatQuotationMoney(quotation.total)}</div>
                    </div>
                    <div className="rounded-lg border border-white/70 bg-white/85 px-3 py-2.5 shadow-sm">
                      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Valid until</div>
                      <div className="font-medium text-foreground">{quotation.valid_until || '-'}</div>
                    </div>
                    <div className={`rounded-lg border px-3 py-2.5 shadow-sm ${getQuotationBookingMetaClass(quotation.order_reference)}`}>
                      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Booking</div>
                      <div>
                        {quotation.order_reference === OPTIMISTIC_BOOKING_REFERENCE ? (
                          <Badge variant="secondary">Creating booking</Badge>
                        ) : hasRealBookingReference(quotation.order_reference) ? (
                          <span className="inline-flex items-center gap-1 font-medium text-foreground">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            {quotation.order_reference}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 font-medium text-foreground">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                            {quotation.order_reference || 'Not booked'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`rounded-lg border px-3 py-2.5 shadow-sm ${getQuotationContractMetaClass(Boolean(quotation.effective_contract_document_url || quotation.contract_document_url))}`}>
                      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Contract</div>
                      <div>
                        {quotation.effective_contract_document_url || quotation.contract_document_url ? (
                          <a href={quotation.effective_contract_document_url || quotation.contract_document_url || '#'} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-sky-700 hover:text-sky-800 hover:underline">
                            <CheckCircle2 className="h-3.5 w-3.5 text-sky-600" />
                            {quotation.contract_document_name || 'Attached'}
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 font-medium text-foreground">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                            Missing
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(quotation.status === 'draft' || quotation.status === 'sent') ? (
                      <Button size="sm" variant="outline" className={getQuotationActionButtonClass('accept')} disabled={activeQuotationAction?.id === quotation.id} onClick={() => acceptQuotationMutation.mutate(quotation.id)}>
                        {activeQuotationAction?.id === quotation.id && activeQuotationAction.action === 'accept' ? 'Accepting...' : 'Accept'}
                      </Button>
                    ) : null}
                    {quotation.status === 'accepted' ? (
                      <Button size="sm" className={getQuotationActionButtonClass('convert')} disabled={activeQuotationAction?.id === quotation.id} onClick={() => convertQuotationMutation.mutate(quotation.id)}>
                        {activeQuotationAction?.id === quotation.id && activeQuotationAction.action === 'convert' ? 'Converting...' : 'Convert'}
                      </Button>
                    ) : null}
                    {quotation.effective_contract_document_url || quotation.contract_document_url ? (
                      <Button size="sm" variant="outline" className={getQuotationActionButtonClass('contract')} asChild>
                        <a href={quotation.effective_contract_document_url || quotation.contract_document_url || '#'} target="_blank" rel="noreferrer">Open Contract</a>
                      </Button>
                    ) : null}
                    {hasRealBookingReference(quotation.order_reference) ? (
                      <Button size="sm" variant="outline" className={getQuotationActionButtonClass('booking')} asChild>
                        <Link to={withServiceInstanceId(`/services/orders/${quotation.order_reference}`, serviceInstanceId)}>Open Booking</Link>
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Jump directly to core sales workflow screens.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className={getQuotationQuickActionClass('neutral')}>
            <Link to={withServiceInstanceId('/services/quotations', serviceInstanceId)}>Quotations</Link>
          </Button>
          <Button asChild variant="outline" className={getQuotationQuickActionClass('open')}>
            <Link to={withServiceInstanceId(buildCommerceQuotationPresetPath('open-quotes'), serviceInstanceId)}>Open Quotes</Link>
          </Button>
          <Button asChild variant="outline" className={getQuotationQuickActionClass('contract')}>
            <Link to={withServiceInstanceId(buildCommerceQuotationPresetPath('needs-contract'), serviceInstanceId)}>Needs Contract</Link>
          </Button>
          <Button asChild variant="outline" className={getQuotationQuickActionClass('ready')}>
            <Link to={withServiceInstanceId(buildSalesDashboardPresetPath('converted'), serviceInstanceId)}>Converted View</Link>
          </Button>
          <Button asChild variant="outline" className={getQuotationQuickActionClass('ready')}>
            <Link to={withServiceInstanceId('/services/orders', serviceInstanceId)}>Bookings</Link>
          </Button>
          <Button asChild variant="outline" className={getQuotationQuickActionClass('contract')}>
            <Link to={withServiceInstanceId('/finance/invoice-payments', serviceInstanceId)}>Payments</Link>
          </Button>
          <Button asChild variant="outline" className={getQuotationQuickActionClass('open')}>
            <Link to={withServiceInstanceId('/planner/planner-model', serviceInstanceId)}>Follow Ups</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
